import { useRef, useEffect, useState, useMemo } from 'react';
import styled from 'styled-components';
import { DateTime } from 'luxon';
import { useQuery } from 'react-query';
import { SelectButton } from 'primereact/selectbutton';
import { TreeTable } from 'primereact/treetable';
import { OverlayPanel } from 'primereact/overlaypanel';
import { Column } from 'primereact/column';
import { Messages } from 'primereact/messages';
import { Calendar } from 'primereact/calendar';
import { ProgressBar } from 'primereact/progressbar';
import _findIndex from 'lodash.findindex';
import _find from 'lodash.find';
import { Button } from 'primereact/button';
import ReactRouterPrompt from 'react-router-prompt';
import { getMeasurementsLookup, getIsConfirmation } from '../../services/measurement';
import MillSummary from './millSummary';
import EditCellModal from './editCellModal';
import { useConfirmAllEntries, useSubmitChanges, useSaveUserSettings } from '../../services/mutations';
import './tooltipStyle.css';
import { getFormattedNumberStr } from '../../utils/number';
import {
  getReasonStrById,
  EditableValue,
  ValueWrapper,
  ValueUpdateInfo,
  TableWrapper,
  ActionsPanel,
  FilterWrapper,
  Filters,
  BtnsWrapper,
  StaticValue,
  CONSUMPTION_VIEW_HIDDEN_COLUMNS_KEY,
} from './common';
import UnsavedChangesModal from './unsavedChangesModal';
import { useReportDetailStore } from '.';
import DiscardChangesModal from './discardChangesModal';
import UnsavedChangesNote from './unsavedChangesNote';
import ConfirmAllValuesModal from './confirmAllValuesModal';
import ExportToExcelButton from '../Excel/exportToExcelButton';
import { TabName } from '../Excel/constants';
import { CategoriesEnum } from '../../common/enum';
import ColumnSelector from './ColumnSelector';
import { useColumnPreferences } from './useColumnPreferences';

const TrueIcon = styled.i`
  font-size: 1.25rem;

  color: ${({ theme }) => theme.colors.positive};
`;

const WarningIcon = styled.i`
  font-size: 1.25rem;
  color: ${({ theme }) => theme.colors.attention};
  cursor: pointer;
`;

const Label = styled.p`
  font-weight: 700;
`;

const FormulaCell = styled.span`
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &:hover {
    cursor: pointer;
    text-decoration: underline;
  }

  .p-overlaypanel .p-overlaypanel-content {
    font-size: 0.75rem;
    max-width: 400px;
  }
`;

const IconCell = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
`;

const LoadingOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.75);
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  color: white;
  font-size: 1.2em;
  z-index: 1000;
  // display: none;

  .load-icon {
    font-size: 3em;
    display: flex;
    align-items: center;

    .pi {
      font-size: 32px;
    }
  }

  .load-message {
    text-align: left;
    max-width: 75vw;
    display: flex;
    flex-direction: column;
    margin-left: 20px;
    line-height: 1.5em;
  }
`;

const categoriesData = [
  { id: 1, name: CategoriesEnum.Electricity },
  { id: 2, name: CategoriesEnum.Heat },
  { id: 3, name: CategoriesEnum.Water },
];

function ConsumptionView({
  placeCode,
  reasonsData,
  tabName,
}: {
  placeCode: string;
  reasonsData: any[];
  tabName: TabName;
}) {
  const [selectedItem, setSelectedItem] = useState(null);
  const [showDiscardConfirmation, setShowDiscardConfirmation] = useState<boolean>(false);
  const [showConfirmValuesModal, setShowConfirmValuesModal] = useState<boolean>(false);
  const [gridData, setGridData] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState<CategoriesEnum | null>(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
  const [selectedYearMonth, setSelectedYearMonth] = useState(DateTime.now().minus({ months: 1 }).toJSDate());
  const messages = useRef<Messages>(null);
  const submitChanges = useSubmitChanges();
  const confirmEntries = useConfirmAllEntries();

  const { updatedCells, resultToast, setUpdatedCells, hasUnsavedData, userPermissions } = useReportDetailStore();

  const getYearMonthNumber = (value: Date) =>
    Number(value.getFullYear().toString() + String(value.getMonth() + 1).padStart(2, '0'));

  const { data: isConfirmed, refetch: isConfirmationRefetch } = useQuery(
    ['confirmationView', placeCode],
    () => getIsConfirmation(placeCode, Number(selectedCategoryId), getYearMonthNumber(selectedYearMonth)),
    { enabled: !!selectedCategory },
  );

  const { data, isLoading, isFetching, error, refetch } = useQuery(
    ['consumptionView', placeCode],
    () => getMeasurementsLookup(placeCode, selectedCategory || '', getYearMonthNumber(selectedYearMonth)),
    { enabled: !!selectedCategory },
  );

  const formulaCellTemplate = (rowData: any) => {
    const op = useRef<OverlayPanel>(null);
    const { formula } = rowData.data;

    if (!formula) {
      return '-';
    }

    return (
      <FormulaCell onClick={(e) => op.current?.toggle(e)}>
        <span>{formula}</span>
        <OverlayPanel
          className="em-custom-overlaypanel"
          ref={op}
          style={{ fontSize: '12px', marginRight: '16px' }}
          showCloseIcon
        >
          {formula}
        </OverlayPanel>
      </FormulaCell>
    );
  };

  const wpsProductionFormulaCellTemplate = (rowData: any) => {
    const op = useRef<OverlayPanel>(null);
    const { wpsProductionFormula } = rowData.data;

    if (!wpsProductionFormula) {
      return '-';
    }

    return (
      <FormulaCell onClick={(e) => op.current?.toggle(e)}>
        <span>{wpsProductionFormula}</span>
        <OverlayPanel
          className="em-custom-overlaypanel"
          ref={op}
          style={{ fontSize: '12px', marginRight: '16px' }}
          showCloseIcon
        >
          {wpsProductionFormula}
        </OverlayPanel>
      </FormulaCell>
    );
  };

  const wpsProductionTemplate = (rowData: any) =>
    rowData.data.wpsProduction ? (
      <span>{getFormattedNumberStr(Math.round(rowData.data.wpsProduction), rowData.data.wpsProductionUnit)}</span>
    ) : (
      '-'
    );

  const oneStreamProductionTemplate = (rowData: any) =>
      rowData.data.oneStreamProductionValue ? (
        <span>{getFormattedNumberStr(Math.round(rowData.data.oneStreamProductionValue), rowData.data.oneStreamProductionUnit)}</span>
      ) : (
        '-'
      );

  const valueCellTemplate = (rowData: any) => {
    const op = useRef<OverlayPanel>(null);
    const updatedValue = _find(updatedCells, (item: any) => item.metricId === rowData.data.metricId);
    const item = updatedValue ? { ...rowData, data: { ...rowData.data, ...updatedValue } } : rowData;
    const updatedByInfo = rowData.data.updatedBy;

    return (
      <ValueWrapper>
        {userPermissions.canUpdateFigures ? (
          <EditableValue onClick={() => setSelectedItem(item)} isUpdated={!!updatedValue}>
            <i className="pi pi-pencil" />
            {getFormattedNumberStr(item.data.value, item.data.metricUnit)}
          </EditableValue>
        ) : (
          <StaticValue>{getFormattedNumberStr(item.data.value, item.data.metricUnit)}</StaticValue>
        )}

        {updatedByInfo && (
          <>
            <i className="cellChangeInfo pi pi-info-circle" onClick={(e) => op.current?.toggle(e)} />
            <OverlayPanel
              className="em-custom-overlaypanel"
              ref={op}
              style={{ fontSize: '12px', marginRight: '16px' }}
              showCloseIcon
            >
              <ValueUpdateInfo>
                <p>Last value update:</p>
                <span>User:</span>
                <span>{updatedByInfo.user}</span>
                <span>Reason:</span>
                <span>{getReasonStrById(updatedByInfo?.reasonId, reasonsData)}</span>
                <span>Updated at:</span>
                <span>
                  {updatedByInfo.createdAt
                    ? DateTime.fromISO(updatedByInfo.createdAt).toLocaleString({
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: 'numeric',
                        minute: 'numeric',
                        second: 'numeric',
                      })
                    : '-'}
                </span>
              </ValueUpdateInfo>
            </OverlayPanel>
          </>
        )}
      </ValueWrapper>
    );
  };

  const missingSensorsCellTemplate = (rowData: any) => {
    const { missingSensors } = rowData.data;
    const op2 = useRef<OverlayPanel>(null);

    if (missingSensors.length > 0) {
      return (
        <IconCell>
          <WarningIcon className="pi pi-exclamation-triangle" onClick={(e) => op2.current?.toggle(e)} />
          <OverlayPanel
            className="em-custom-overlaypanel"
            ref={op2}
            style={{ fontSize: '12px', marginRight: '16px' }}
            showCloseIcon
          >
            <Label>Missing sensors:</Label>
            <span>{missingSensors.map((item: any) => item.tagName).join(', ')}</span>
          </OverlayPanel>
        </IconCell>
      );
    }

    return (
      <IconCell>
        <TrueIcon className="pi pi-check-circle" />
      </IconCell>
    );
  };

  const handleUpdatedCell = (values: any) => {
    const selectedMetricId = selectedItem?.data.metricId;

    const objIdx = _findIndex(updatedCells, (item: any) => item.metricId === selectedMetricId);

    if (objIdx !== -1) {
      const tmpUpdatedCells: any[] = updatedCells;
      const cell: any = updatedCells[objIdx];

      tmpUpdatedCells[objIdx] = {
        ...cell,
        value: values.value,
        reason: values.reason,
      };
      setUpdatedCells(tmpUpdatedCells);
    } else {
      setUpdatedCells([...updatedCells, { metricId: selectedMetricId, ...values }]);
    }

    setSelectedItem(null);
  };

  const handleSubmitChanges = () => {
    submitChanges.mutate(
      {
        code: placeCode,
        yearMonth: getYearMonthNumber(selectedYearMonth),
        data: updatedCells,
        metricTypeID: selectedCategoryId,
      },
      {
        onSuccess: () => {
          resultToast.current?.show({
            severity: 'success',
            summary: 'Submit changes',
            detail: 'Changes has been successfully submitted.',
            life: 3000,
          });
        },
        onError: () => {
          resultToast.current?.show({
            severity: 'error',
            summary: 'Submit changes',
            detail: 'An error has occurred while submitting changes.',
            life: 3000,
          });
        },
      },
    );
  };

  const handleDiscard = () => {
    setShowDiscardConfirmation(false);
    setUpdatedCells([]);
    resultToast.current?.show({
      severity: 'success',
      summary: 'Discard changes',
      detail: 'Changes has been successfully discarded.',
      life: 3000,
    });
  };

  const handleConfirmValues = () => {
    setShowConfirmValuesModal(false);
    confirmEntries.mutate(
      { code: placeCode, yearMonth: getYearMonthNumber(selectedYearMonth), metricTypeId: selectedCategoryId },
      {
        onSuccess: () => {
          resultToast.current?.show({
            severity: 'success',
            summary: 'Confirm report entries',
            detail: 'All report entries has been successfully confirmed.',
            life: 3000,
          });
          isConfirmationRefetch();
        },
        onError: () => {
          resultToast.current?.show({
            severity: 'error',
            summary: 'Confirm report entries',
            detail: 'An error has occurred while confirming report entries.',
            life: 3000,
          });
        },
      },
    );
  };

  useEffect(() => {
    if (error) {
      messages.current?.show({ severity: 'error', summary: 'An error has occurred while loading data!', sticky: true });
    }
  }, [error]);

  useEffect(() => {
    if (data) {
      setUpdatedCells([]);

      const tempData = data.items.map((item) => ({
        key: item.code,
        data: {
          label: item.name,
          level: 2,
          metricType: item.metricType,
          metricUnit: item.metricUnit,
          metricId: item.metricId,
          value: item.value,
          formula: item.formula,
          missingSensors: item.missingSensors,
          wpsProduction: item.wpsProduction,
          wpsProductionUnit: item.wpsProductionUnit,
          wpsProductionFormula: item.wpsProductionFormula,
          oneStreamProductionValue: item.oneStreamProductionValue,
          oneStreamProductionUnit: item.oneStreamProductionUnit,
          updatedBy: item.createdBy
            ? {
                user: item.createdBy,
                reasonId: item.reasonId,
                createdAt: item.createdAt,
              }
            : null,
        },
        children: [
          ...item.items.map((ltItem: any) => ({
            key: ltItem.code,
            data: {
              label: ltItem.name,
              level: 3,
              metricType: ltItem.metricType,
              metricUnit: ltItem.metricUnit,
              metricId: ltItem.metricId,
              value: ltItem.value,
              formula: ltItem.formula,
              missingSensors: ltItem.missingSensors,
              wpsProduction: ltItem.wpsProduction,
              wpsProductionUnit: ltItem.wpsProductionUnit,
              wpsProductionFormula: ltItem.wpsProductionFormula,
              oneStreamProductionValue: ltItem.oneStreamProductionValue,
              oneStreamProductionUnit: ltItem.oneStreamProductionUnit,
              updatedBy: ltItem.createdBy
                ? {
                    user: ltItem.createdBy,
                    reasonId: ltItem.reasonId,
                    createdAt: ltItem.createdAt,
                  }
                : null,
            },
          })),
        ],
      }));

      setGridData(tempData);
    }
  }, [data]);

  useEffect(() => {
    if (selectedCategory) {
      refetch();
      isConfirmationRefetch();
      setUpdatedCells([]);
    }
  }, [selectedCategory, selectedYearMonth, refetch]);

  useEffect(() => {
    if (categoriesData.length > 0 && !selectedCategory) {
      setSelectedCategory(categoriesData[0].name);
      setSelectedCategoryId(categoriesData[0].id ?? null);
    }
  }, [categoriesData]);

  const submittingChanges = submitChanges.isLoading || confirmEntries.isLoading;

  // Optional columns with categories in which they're are visible.
  const optionalColumns = useMemo(
    () => [
      {
        field: 'formula',
        header: 'Formula',
        body: formulaCellTemplate,
        visibleFor: [CategoriesEnum.Electricity, CategoriesEnum.Heat, CategoriesEnum.Water],
      },
      {
        field: 'oneStreamProduction',
        header: 'One Stream Production',
        body: oneStreamProductionTemplate,
        visibleFor: [CategoriesEnum.Electricity, CategoriesEnum.Heat],
      },
      {
        field: 'wpsProduction',
        header: 'WPS Production',
        body: wpsProductionTemplate,
        visibleFor: [CategoriesEnum.Electricity, CategoriesEnum.Heat],
      },
      {
        field: 'wpsProductionFormula',
        header: 'WPS Production Formula',
        body: wpsProductionFormulaCellTemplate,
        visibleFor: [CategoriesEnum.Electricity, CategoriesEnum.Heat],
      },
    ],
    []
  );

  const { filteredColumns, hiddenColumns, displayedColumns } = useColumnPreferences(optionalColumns, selectedCategory, CONSUMPTION_VIEW_HIDDEN_COLUMNS_KEY);

  const saveHiddenColumns = useSaveUserSettings(CONSUMPTION_VIEW_HIDDEN_COLUMNS_KEY);

  // Handle column selection changes.
  const handleColumnSelectionChange = (newHiddenFields: string[]) => {
    if (selectedCategory) {
      saveHiddenColumns.mutate({
        category: selectedCategory,
        hidden: newHiddenFields,
      });
    }
  };

  return (
    <>
      {submittingChanges && data?.isOlderThanTwoMonth && (
        <LoadingOverlay>
          <div className="load-icon"><i className="pi pi-spin pi-spinner"></i></div>
          <div className="load-message">
            <div>
              Saving changes...
            </div>
            <div>
              Keep in mind that saving values older than 2 months can take up to 60 seconds. Do not leave the page.
            </div>
          </div>        
        </LoadingOverlay>
      )}
      <Messages ref={messages} />
      <ReactRouterPrompt when={hasUnsavedData()}>
        {({ isActive, onConfirm, onCancel }) => (
          <UnsavedChangesModal isOpen={isActive} onConfirm={onConfirm} onCancel={onCancel} />
        )}
      </ReactRouterPrompt>

      <DiscardChangesModal
        isOpen={showDiscardConfirmation}
        onConfirm={handleDiscard}
        onCancel={() => setShowDiscardConfirmation(false)}
      />

      <ConfirmAllValuesModal
        isOpen={showConfirmValuesModal}
        onConfirm={handleConfirmValues}
        onCancel={() => setShowConfirmValuesModal(false)}
      />

      {selectedItem && (
        <EditCellModal
          item={selectedItem}
          defaultReason={3}
          reasonsData={reasonsData}
          onSubmit={handleUpdatedCell}
          onClose={() => setSelectedItem(null)}
        />
      )}
      {isLoading && <ProgressBar mode="indeterminate" style={{ height: '4px', marginBottom: '16px' }} />}

      {data && (
        <div>
          <ActionsPanel>
            <FilterWrapper>
              <Filters>
                <div>
                  <label htmlFor="category">Filter by category</label>
                  <SelectButton
                    id="category"
                    disabled={isFetching || hasUnsavedData() || submittingChanges}
                    value={selectedCategory}
                    onChange={(e) => {
                      if (e.value != null) {
                        setSelectedCategory(e.value);
                        setSelectedCategoryId(categoriesData.find((c) => c.name === e.value)?.id);
                      }
                    }}
                    optionValue="name"
                    optionLabel="name"
                    options={categoriesData}
                  />
                </div>
                <div>
                  <label htmlFor="fromYearmonth">Filter by year month</label>
                  <Calendar
                    id="fromYearmonth"
                    view="month"
                    dateFormat="MM, yy"
                    readOnlyInput
                    keepInvalid
                    value={selectedYearMonth}
                    disabled={isFetching || hasUnsavedData() || submittingChanges}
                    onChange={(e) => {
                      setSelectedYearMonth(e.value);
                    }}
                    hideOnDateTimeSelect
                  />
                </div>
              </Filters>
              {hasUnsavedData() && (
                <UnsavedChangesNote onDiscard={() => setShowDiscardConfirmation(true)} onSubmit={handleSubmitChanges} />
              )}
            </FilterWrapper>
            <div>
              <BtnsWrapper>
                {userPermissions.canUpdateFigures && (
                  <>
                    <Button
                      label="Discard changes"
                      size="small"
                      severity="warning"
                      disabled={!hasUnsavedData() || submittingChanges || isFetching}
                      onClick={() => setShowDiscardConfirmation(true)}
                    />
                    <Button
                      label="Save changes"
                      size="small"
                      onClick={handleSubmitChanges}
                      loading={submittingChanges}
                      disabled={!hasUnsavedData() || submittingChanges || isFetching}
                    />
                    <Button
                      label="Confirm values"
                      icon="pi pi-check-circle"
                      size="small"
                      onClick={() => setShowConfirmValuesModal(true)}
                      loading={submittingChanges}
                      disabled={submittingChanges || isFetching || isConfirmed}
                    />
                  </>
                )}
                <ExportToExcelButton
                  placeCode={placeCode}
                  tabName={tabName}
                  sourceName=''
                  selectedTime={getYearMonthNumber(selectedYearMonth)}
                  isDisabled={submittingChanges || isFetching}
                />
              </BtnsWrapper>
            </div>
          </ActionsPanel>

          <MillSummary
            data={data}
            updatedCells={updatedCells}
            reasonsData={reasonsData}
            onUpdate={(item: any) => setSelectedItem(item)}
            isWater={selectedCategory == CategoriesEnum.Water}
          />

          <ColumnSelector
            value={hiddenColumns}   
            options={filteredColumns}     
            onChange={handleColumnSelectionChange}
          />

          <TableWrapper>
            <TreeTable
              header={null}
              loading={isFetching}
              rowHover
              value={gridData}
              tableStyle={{ minWidth: '50rem' }}
              resizableColumns
              showGridlines
              columnResizeMode="expand"
            >
              {/* Always-visible columns */}
              <Column
                field="label"
                header="Plant Section"
                expander
                body={(rowData) => (rowData.data?.label ? `${rowData.data.label} (${rowData.key})` : '')}
                style={{ width: '450px' }}
              />
              <Column field="missingSensors" header="" body={missingSensorsCellTemplate} style={{ width: '40px' }} />
              <Column field="value" header="Value" body={valueCellTemplate} style={{ width: '150px' }} />
              {/* Dynamically rendered columns */}
              {displayedColumns.map((col) => (
                <Column key={col.field} field={col.field} header={col.header} body={col.body} />
              ))}
            </TreeTable>
          </TableWrapper>
        </div>
      )}
    </>
  );
}

export default ConsumptionView;
